/* eslint-disable */
import React, { useState, useEffect, useCallback } from "react";
import { Table, Tag, Input, Select, DatePicker, message, Button } from "antd";
import Header from "../layouts/Header";
import Sidebar from "../layouts/Sidebar";
import { auditApi } from "../../apis";
import { humanizeAction, humanizeDiff, toneColor } from "./humanize";

const { RangePicker } = DatePicker;

const methodColor = (m) =>
  ({ POST: "green", PATCH: "gold", PUT: "gold", DELETE: "red" }[m] || "default");

const statusColor = (s) =>
  s >= 500 ? "red" : s >= 400 ? "volcano" : "green";

const AuditLogPage = () => {
  const [menu, setMenu] = useState(false);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [filters, setFilters] = useState({
    actor: "",
    method: undefined,
    entityType: "",
    startDate: undefined,
    endDate: undefined,
  });
  // Rows whose raw "technical details" are currently revealed in the expand panel.
  const [showRaw, setShowRaw] = useState({});

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const query = { page, limit };
      if (filters.actor) query.actor = filters.actor;
      if (filters.method) query.method = filters.method;
      if (filters.entityType) query.entityType = filters.entityType;
      if (filters.startDate) query.startDate = filters.startDate;
      if (filters.endDate) query.endDate = filters.endDate;
      const res = await auditApi.getAll(query);
      setRows(res.data || []);
      setTotal(res.total || 0);
    } catch {
      message.error("Failed to load audit log");
    } finally {
      setLoading(false);
    }
  }, [page, limit, filters]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = [
    {
      title: "Time",
      dataIndex: "createdAt",
      width: 170,
      render: (v) =>
        new Date(v).toLocaleString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
    },
    {
      title: "Actor",
      dataIndex: "actorEmail",
      render: (v, r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{v || "—"}</div>
          <div style={{ fontSize: 12, color: "#9ca3af" }}>{r.actorRole}</div>
        </div>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (v, r) => {
        const { text, tone } = humanizeAction(r);
        const failed = r.statusCode != null && r.statusCode >= 400;
        return (
          <div>
            <div
              style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 500 }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: {
                    green: "#16a34a",
                    blue: "#2563eb",
                    gold: "#d97706",
                    red: "#dc2626",
                  }[toneColor(tone)] || "#9ca3af",
                  flexShrink: 0,
                }}
              />
              <span>{text}</span>
              {failed && <Tag color="red" style={{ marginLeft: 2 }}>Failed</Tag>}
            </div>
            <div
              style={{
                fontFamily: "monospace",
                fontSize: 11,
                color: "#9ca3af",
                marginLeft: 16,
                marginTop: 2,
              }}
            >
              {r.method} {v.replace(/^[A-Z]+ /, "")}
            </div>
          </div>
        );
      },
    },
    { title: "Entity", dataIndex: "entityType", width: 120 },
    {
      title: "Status",
      dataIndex: "statusCode",
      width: 90,
      render: (s) => (s == null ? "—" : <Tag color={statusColor(s)}>{s}</Tag>),
    },
    { title: "IP", dataIndex: "ip", width: 130 },
  ];

  return (
    <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
      <Header onMenuClick={() => setMenu(!menu)} />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="page-header">
            <div className="content-page-header">
              <h5>Audit Log</h5>
            </div>
          </div>

          <div className="card" style={{ borderRadius: 14 }}>
            <div className="card-body">
              {/* Filters */}
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  flexWrap: "wrap",
                  marginBottom: 16,
                }}
              >
                <Input.Search
                  placeholder="Actor email"
                  allowClear
                  style={{ width: 240 }}
                  onSearch={(v) => {
                    setPage(1);
                    setFilters((f) => ({ ...f, actor: v }));
                  }}
                />
                <Select
                  placeholder="Method"
                  allowClear
                  style={{ width: 130 }}
                  value={filters.method}
                  onChange={(v) => {
                    setPage(1);
                    setFilters((f) => ({ ...f, method: v }));
                  }}
                  options={[
                    { value: "POST", label: "POST" },
                    { value: "PATCH", label: "PATCH" },
                    { value: "PUT", label: "PUT" },
                    { value: "DELETE", label: "DELETE" },
                  ]}
                />
                <Input
                  placeholder="Entity (e.g. reservations)"
                  allowClear
                  style={{ width: 200 }}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPage(1);
                    setFilters((f) => ({ ...f, entityType: v }));
                  }}
                />
                <RangePicker
                  showTime
                  onChange={(vals) => {
                    setPage(1);
                    setFilters((f) => ({
                      ...f,
                      startDate: vals?.[0]?.toISOString(),
                      endDate: vals?.[1]?.toISOString(),
                    }));
                  }}
                />
              </div>

              <Table
                rowKey="id"
                loading={loading}
                columns={columns}
                dataSource={rows}
                size="middle"
                pagination={{
                  current: page,
                  pageSize: limit,
                  total,
                  showSizeChanger: true,
                  onChange: (p, ps) => {
                    setPage(p);
                    setLimit(ps);
                  },
                }}
                expandable={{
                  expandedRowRender: (r) => {
                    const diff = humanizeDiff(r.before, r.after);
                    const raw = showRaw[r.id];
                    return (
                      <div style={{ fontSize: 13 }}>
                        {/* Human-readable changes */}
                        {diff.mode === "diff" && (
                          <div>
                            <strong>Changes</strong>
                            <div style={{ marginTop: 6 }}>
                              {diff.rows.map((row, i) => (
                                <div
                                  key={i}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    padding: "3px 0",
                                  }}
                                >
                                  <span style={{ color: "#6b7280", minWidth: 130 }}>
                                    {row.label}
                                  </span>
                                  <span style={{ color: "#9ca3af" }}>{row.from}</span>
                                  <span style={{ color: "#9ca3af" }}>→</span>
                                  <span style={{ fontWeight: 600, color: "#111827" }}>
                                    {row.to}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {diff.mode === "create" && (
                          <div>
                            <strong>Details</strong>
                            <div style={{ marginTop: 6 }}>
                              {diff.rows.map((row, i) => (
                                <div
                                  key={i}
                                  style={{
                                    display: "flex",
                                    gap: 8,
                                    padding: "3px 0",
                                  }}
                                >
                                  <span style={{ color: "#6b7280", minWidth: 130 }}>
                                    {row.label}
                                  </span>
                                  <span style={{ fontWeight: 600, color: "#111827" }}>
                                    {row.value}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {diff.mode === "empty" && (
                          <div style={{ color: "#9ca3af" }}>
                            No field changes recorded.
                          </div>
                        )}

                        {/* Technical details toggle */}
                        <Button
                          type="link"
                          size="small"
                          style={{ padding: "8px 0 0", height: "auto" }}
                          onClick={() =>
                            setShowRaw((s) => ({ ...s, [r.id]: !s[r.id] }))
                          }
                        >
                          {raw ? "Hide technical details" : "Show technical details"}
                        </Button>

                        {raw && (
                          <div style={{ marginTop: 6 }}>
                            <div style={{ color: "#6b7280", marginBottom: 6 }}>
                              <Tag color={methodColor(r.method)}>{r.method}</Tag>
                              <span style={{ fontFamily: "monospace" }}>
                                {r.action?.replace(/^[A-Z]+ /, "")}
                              </span>
                              {r.statusCode != null && (
                                <Tag
                                  color={statusColor(r.statusCode)}
                                  style={{ marginLeft: 6 }}
                                >
                                  {r.statusCode}
                                </Tag>
                              )}
                            </div>
                            {r.before && (
                              <div style={{ marginBottom: 8 }}>
                                <strong>Before:</strong>
                                <pre style={preStyle}>
                                  {JSON.stringify(r.before, null, 2)}
                                </pre>
                              </div>
                            )}
                            <div>
                              <strong>Payload / after:</strong>
                              <pre style={preStyle}>
                                {JSON.stringify(r.after, null, 2)}
                              </pre>
                            </div>
                            <div style={{ color: "#9ca3af", marginTop: 6 }}>
                              {r.userAgent}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  },
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const preStyle = {
  background: "#f9fafb",
  border: "1px solid #f3f4f6",
  borderRadius: 8,
  padding: 12,
  margin: "4px 0 0",
  maxHeight: 240,
  overflow: "auto",
};

export default AuditLogPage;
